export type StationId =
  | 'chicken-crates'
  | 'chicken-board'
  | 'marinade'
  | 'fridge'
  | 'rice-bags'
  | 'sink'
  | 'veg-basket'
  | 'veg-board'
  | 'oven'
  | 'serve';

export type AudioEvent =
  | 'ui'
  | 'pickup'
  | 'chop'
  | 'splash'
  | 'pour'
  | 'fridge'
  | 'timer-ready'
  | 'oven'
  | 'complete'
  | 'deliver'
  | 'fail';

export interface OrderState {
  id: number;
  customer: string;
  chickens: number;
  remaining: number;
  total: number;
  status: 'active' | 'ready' | 'delivered' | 'expired';
  reaction?: string;
}

export type ConversationStatus = OrderState['status'];

export interface ConversationMessage {
  id: string;
  sender: 'customer' | 'restaurant';
  text: string;
  sentAt: number;
}

export interface CustomerConversation {
  orderId: number;
  customer: string;
  chickens: number;
  status: ConversationStatus;
  messages: ConversationMessage[];
  rating?: number;
  deliverySeconds?: number;
}

export interface SessionStats {
  totalOrders: number;
  deliveredOrders: number;
  expiredOrders: number;
  averageDeliverySeconds: number;
  bestDeliverySeconds?: number;
  positiveReviews: number;
  negativeReviews: number;
  finalScore: number;
  finalReputation: number;
}

export interface SessionState {
  duration: number;
  remaining: number;
  configuredDuration: number;
  ended: boolean;
  settingsVisible: boolean;
  stats?: SessionStats;
}

export interface ChickenState {
  cratesTaken: number;
  onBoard: number;
  cutHalves: number;
  marinadeCount: number;
  marinadeRemaining: number;
  chilledCount: number;
  chillRemaining: number;
  ovenReady: number;
}

export interface RiceState {
  bagPlaced: boolean;
  bowlFilled: boolean;
  washCount: number;
  washCooldown: number;
  ovenReady: boolean;
}

export interface VegetableState {
  placed: boolean;
  revealed: boolean;
  chopCount: number;
  chopCooldown: number;
  ovenReady: boolean;
}

export interface OvenState {
  loaded: boolean;
  cooking: boolean;
  remaining: number;
  platterReady: boolean;
}

export interface KitchenSnapshot {
  order: OrderState;
  chicken: ChickenState;
  rice: RiceState;
  vegetables: VegetableState;
  oven: OvenState;
  score: number;
  reputation: number;
  combo: number;
  completedOrders: number;
  conversations: CustomerConversation[];
  session: SessionState;
  paused: boolean;
  muted: boolean;
  tutorialVisible: boolean;
  nextStation: StationId;
  attentionStations: StationId[];
  hint: string;
  toast?: string;
}

export type UiIntent =
  | { type: 'station'; station: StationId }
  | { type: 'pause-toggle' }
  | { type: 'mute-toggle' }
  | { type: 'settings-show' }
  | { type: 'settings-hide' }
  | { type: 'set-session-duration'; minutes: number }
  | { type: 'new-session' }
  | { type: 'restart' }
  | { type: 'tutorial-dismiss' }
  | { type: 'tutorial-show' };

export const STATION_LABELS: Record<StationId, string> = {
  'chicken-crates': 'كراتين الدجاج',
  'chicken-board': 'تقطيع الدجاج',
  marinade: 'التتبيلة',
  fridge: 'الثلاجة',
  'rice-bags': 'أكياس الرز',
  sink: 'غسل الرز',
  'veg-basket': 'الخضار',
  'veg-board': 'تقطيع الخضار',
  oven: 'فرن المندي',
  serve: 'تسليم المندوب',
};

